import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import moment from 'moment'
import { MDBIcon } from 'mdb-react-ui-kit'
import { firebaseAuth } from './../../api/FirebaseAuth'
import EchoService from './../../shared/services/Echo.service'
import UserProfileService from './../../shared/services/UserProfile.service'
import { RequiresAuthGuard, RequiresUsertypesGuard } from './../../shared/router/guards'
import BigScreenBoard from './components/BigScreenBoard/BigScreenBoard.component'
import BigScreenSettings from './components/BigScreenSettings/BigScreenSettings.component'
import BigScreenStore, {
  BOARDS,
  PERIODS,
  METRICS,
  GROUPINGS,
  getPeriodRange,
  parseSettings,
  toSearch,
} from './store/BigScreen.store'
import { BIG_SCREEN_USERTYPES } from './variables'
import './BigScreen.page.scss'

const CONTROLS_HIDE_MS = 5000
// Live sale events arrive in bursts (a multi-coverage sale is several events).
const LIVE_REFETCH_DEBOUNCE_MS = 5000
const PERIOD_CHECK_MS = 60000

const labelOf = (options, value) => (options.find(o => `${o.value}` === `${value}`) || {}).label

const describeBoard = (board, settings, rows) => {
  const scope = UserProfileService.isA(['agency-owner', 'internal-admin', 'internal-staff', 'system-admin'])
    ? 'Company-wide'
    : 'My Team'

  switch (board) {
    case 'points':
      return { title: 'Points', subtitle: `${scope} · Sig Agents`, unit: 'points', group: 'user_id' }
    case 'av':
      return { title: 'AV', subtitle: `${scope} · Sig Agents`, unit: 'av', group: 'user_id' }
    case 'enrolled':
      return { title: 'Enrolled Agents', subtitle: `${scope} · Recruiters`, unit: 'count', group: 'user_id' }
    case 'leaderboard':
    default:
      return {
        title: `${labelOf(METRICS, settings.metric)} Leaderboard`,
        subtitle: [
          labelOf(GROUPINGS, settings.group),
          // A division/team filter leaves only that division's or team's rows.
          settings.group === 'user_id' && settings.division_id && rows?.[0]?.div_name,
          settings.group === 'sub_agent_id' && settings.team_id && rows?.[0]?.team_name,
          settings.category,
        ]
          .filter(Boolean)
          .join(' · '),
        unit: settings.metric,
        group: settings.group,
      }
  }
}

const describePeriod = (period, { start, stop }) => {
  const from = moment(start, 'YYYY-MM-DD'),
    to = moment(stop, 'YYYY-MM-DD')
  const dates = start === stop ? from.format('MMM D, YYYY') : `${from.format('MMM D')} – ${to.format('MMM D, YYYY')}`
  return `${labelOf(PERIODS, period)} · ${dates}`
}

const AccessDenied = () => {
  const history = useHistory()
  return (
    <main id='BigScreenPage' className='is-denied'>
      <div className='big-screen-denied' role='alert'>
        <MDBIcon icon='lock' size='3x' />
        <h1>Access denied</h1>
        <p>Big Screen is available to agency leaders, their admins and USABG staff.</p>
        <button type='button' className='big-screen-btn' onClick={() => history.push('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    </main>
  )
}

const BigScreen = observer(() => {
  const history = useHistory(),
    { search } = useLocation(),
    settings = useMemo(() => parseSettings(search), [search]),
    canonicalSearch = toSearch(settings),
    boardsKey = settings.boards.join(','),
    [boardIndex, setBoardIndex] = useState(0),
    [controlsVisible, setControlsVisible] = useState(false),
    [settingsOpen, setSettingsOpen] = useState(false),
    settingsBtnRef = useRef(null)

  // The page address is the only copy of the settings: bookmarking it reopens this exact screen.
  useEffect(() => {
    if (search !== canonicalSearch) history.replace(`/big-screen${canonicalSearch}`)
  }, [search, canonicalSearch, history])

  const updateSettings = useCallback(next => history.replace(`/big-screen${toSearch(next)}`), [history])

  useEffect(() => {
    BigScreenStore.applySettings(settings)
  }, [settings])

  useEffect(() => {
    BigScreenStore.start()
    const periodCheck = setInterval(BigScreenStore.checkPeriodRollover, PERIOD_CHECK_MS)
    return () => {
      clearInterval(periodCheck)
      BigScreenStore.stop()
    }
  }, [])

  // New and changed sales re-rank the boards; the store's timed refresh covers what the feed misses.
  useEffect(() => {
    let channel = null,
      debounce = null,
      cancelled = false

    const onFeedChanged = () => {
      clearTimeout(debounce)
      debounce = setTimeout(BigScreenStore.refresh, LIVE_REFETCH_DEBOUNCE_MS)
    }

    const unsubscribeAuth = onAuthStateChanged(firebaseAuth, user => {
      if (!user || channel) return
      EchoService.init()
        .then(echo => {
          if (cancelled || !echo || channel) return
          channel = echo.private('leaderboards.live')
          channel.listen('.feed.changed', onFeedChanged)
        })
        .catch(err => console.warn('[BigScreen] live updates unavailable; using timed refresh only', err))
    })

    return () => {
      cancelled = true
      unsubscribeAuth()
      clearTimeout(debounce)
      if (channel) channel.stopListening('.feed.changed', onFeedChanged)
    }
  }, [])

  useEffect(() => setBoardIndex(0), [boardsKey, settings.rotate])

  useEffect(() => {
    if (settings.boards.length < 2 || !settings.rotate) return
    const rotation = setInterval(() => setBoardIndex(i => (i + 1) % settings.boards.length), settings.rotate * 1000)
    return () => clearInterval(rotation)
  }, [boardsKey, settings.rotate, settings.boards.length])

  useEffect(() => {
    let hideTimer = null
    const reveal = () => {
      setControlsVisible(true)
      clearTimeout(hideTimer)
      hideTimer = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS)
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart']
    events.forEach(evt => window.addEventListener(evt, reveal))
    return () => {
      clearTimeout(hideTimer)
      events.forEach(evt => window.removeEventListener(evt, reveal))
    }
  }, [])

  useEffect(() => {
    // The office support chat bubble would sit on top of the board and the Exit button.
    document.body.classList.add('big-screen-active')
    return () => document.body.classList.remove('big-screen-active')
  }, [])

  const closeSettings = useCallback(() => {
    setSettingsOpen(false)
    settingsBtnRef.current?.focus()
  }, [])

  const board = settings.boards[Math.min(boardIndex, settings.boards.length - 1)],
    rows = BigScreenStore.rowsByBoard[board],
    { title, subtitle, unit, group } = describeBoard(board, settings, rows),
    // Until rows for the current settings arrive, show the period being loaded, not the last one.
    range = BigScreenStore.hasData ? BigScreenStore.range : getPeriodRange(settings.period),
    showControls = controlsVisible || settingsOpen

  const renderBody = () => {
    if (Array.isArray(rows))
      return (
        <BigScreenBoard
          rows={rows}
          unit={unit}
          group={group}
          emptyMessage={board === 'enrolled' ? 'No enrollments this period' : 'No production this period'}
        />
      )

    if (BigScreenStore.refreshFailed)
      return (
        <div className='big-screen-board is-empty'>
          <p>Rankings are unavailable right now.</p>
        </div>
      )

    return (
      <div className='big-screen-board is-empty' role='status'>
        <p>Loading rankings…</p>
      </div>
    )
  }

  return (
    <main id='BigScreenPage' className={showControls ? 'controls-visible' : 'controls-hidden'}>
      {BigScreenStore.refreshFailed && (
        <div className='big-screen-toast' role='alert'>
          Couldn&apos;t refresh — retrying
        </div>
      )}

      <header className='big-screen-header'>
        <div className='big-screen-title'>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <div className='big-screen-meta'>
          <p className='big-screen-period'>{describePeriod(settings.period, range)}</p>
          <p className='big-screen-updated'>
            {BigScreenStore.lastUpdatedAt
              ? `Last updated ${moment(BigScreenStore.lastUpdatedAt).format('h:mm A')}`
              : ''}
          </p>
          {settings.boards.length > 1 && (
            <div className='big-screen-dots' aria-label={`Board ${boardIndex + 1} of ${settings.boards.length}`}>
              {settings.boards.map((b, i) => (
                <span key={b} className={i === boardIndex ? 'is-active' : ''} title={labelOf(BOARDS, b)} />
              ))}
            </div>
          )}
        </div>
      </header>

      {renderBody()}

      <nav className='big-screen-controls' aria-label='Big Screen controls'>
        <button
          type='button'
          className='big-screen-btn'
          ref={settingsBtnRef}
          onClick={() => setSettingsOpen(open => !open)}
        >
          <MDBIcon icon='cog' /> Settings
        </button>
        <button type='button' className='big-screen-btn' onClick={() => history.push('/leaderboards')}>
          <MDBIcon icon='sign-out-alt' /> Exit
        </button>
      </nav>

      {settingsOpen && <BigScreenSettings settings={settings} onChange={updateSettings} onClose={closeSettings} />}
    </main>
  )
})

const BigScreenPage = () => {
  if (!new RequiresAuthGuard(true).authorize()) return <Redirect to='/login' />
  return new RequiresUsertypesGuard(BIG_SCREEN_USERTYPES).authorize() ? <BigScreen /> : <AccessDenied />
}

export default BigScreenPage
